import React from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import { TFunction, useTranslation } from 'react-i18next';

import LoadingProgress from '../components/LoadingProgress/LoadingProgress';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useNoNetworkConnection } from '../hooks';

import APP_ROUTES from './appRoutes';
import ROUTES from '../constants/routes';
import { getSearchParamValue } from '../helpers/path';
import i18n from '../locales/i18n';

import { LANGUAGE_NAMESPACE, LanguageNamespaces } from '@constants/language';

const useHandleLoginError = (
  setLoginError: (error: string) => void,
  translate: TFunction<LanguageNamespaces, undefined>,
) => {
  const errorDescription = decodeURIComponent(
    getSearchParamValue(location.search, 'error_description'),
  );

  if (errorDescription) {
    // Removes prefix of Cognito error description to provide user-friendly message.
    // Example:
    // error description: "Invalid SAML response received PreSignUp failed with error Unable to retrieve user, please contact support."
    // expected message: "Unable to retrieve user, please contact support."
    const errorPrefix = 'PreSignUp failed with error ';
    if (errorDescription.includes(errorPrefix)) {
      const prefixIndex = errorDescription.indexOf(errorPrefix);
      const errorCode = errorDescription
        .substring(prefixIndex + errorPrefix.length)
        .trim()
        .replace('.', '');

      // Look up translation
      if (
        i18n.exists(`notifications.${errorCode}`, { ns: [LANGUAGE_NAMESPACE] })
      ) {
        setLoginError(translate(`notifications.${errorCode}`));
      } else {
        setLoginError(errorCode);
      }
    } else {
      setLoginError(errorDescription);
    }
  }
};

const Routes = () => {
  const { loading, appTheme, setLoginError } = useMytaverse();
  const routes = useRoutes(APP_ROUTES);
  const { pathname } = useLocation();
  const { t: translate } = useTranslation();

  useNoNetworkConnection();
  useHandleLoginError(setLoginError, translate);

  if (loading && (!pathname.includes(ROUTES.LOGIN) || !appTheme)) {
    return <LoadingProgress fullHeight />;
  }

  return <>{routes}</>;
};

export default Routes;
